import { supabase } from "../supabaseClient"

const STORAGE_PREFIX = "ga4-default-templates"
const CHANGE_EVENT_NAME = "ga4-default-template-change"

export const PROPERTY_TYPE_LABELS = {
  PROPERTY_TYPE_ORDINARY: "Standard",
  PROPERTY_TYPE_360: "360",
}

export const DEFAULT_PROPERTY_TYPE = "PROPERTY_TYPE_ORDINARY"

const BASE_MAP = {
  PROPERTY_TYPE_ORDINARY: null,
  PROPERTY_TYPE_360: null,
}

const hasWindow = () => typeof window !== "undefined"

const normalizeContextKey = (contextKey) => {
  if (typeof contextKey !== "string" || !contextKey.trim()) {
    return "global"
  }
  return contextKey.trim()
}

const parseContext = (contextKey) => {
  const normalizedKey = normalizeContextKey(contextKey)
  if (normalizedKey.startsWith("org:")) {
    return {
      normalizedKey,
      organizationId: normalizedKey.slice(4) || null,
      userId: null,
    }
  }
  if (normalizedKey.startsWith("user:")) {
    return {
      normalizedKey,
      organizationId: null,
      userId: normalizedKey.slice(5) || null,
    }
  }
  return {
    normalizedKey,
    organizationId: null,
    userId: null,
  }
}

const getStorageKey = (contextKey) =>
  `${STORAGE_PREFIX}:${normalizeContextKey(contextKey)}`

const normalizeMap = (value) => {
  if (!value || typeof value !== "object") {
    return { ...BASE_MAP }
  }
  const map = { ...BASE_MAP }
  for (const key of Object.keys(BASE_MAP)) {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      const val = value[key]
      map[key] = val == null ? null : val
    }
  }
  return map
}

export const ensureDefaultTemplateMap = (map) => normalizeMap(map)

export const getDefaultTemplateContextKey = ({ orgId, userId }) => {
  if (orgId) return `org:${orgId}`
  if (userId) return `user:${userId}`
  return "global"
}

export const loadDefaultTemplateMap = (contextKey) => {
  if (!hasWindow()) {
    return { ...BASE_MAP }
  }
  try {
    const raw = window.localStorage.getItem(getStorageKey(contextKey))
    if (!raw) return { ...BASE_MAP }
    const parsed = JSON.parse(raw)
    return normalizeMap(parsed)
  } catch (err) {
    console.warn("[GA4] Failed to load default template map", err)
    return { ...BASE_MAP }
  }
}

const saveDefaultTemplateMap = (contextKey, map) => {
  if (!hasWindow()) return
  try {
    const normalized = normalizeMap(map)
    window.localStorage.setItem(
      getStorageKey(contextKey),
      JSON.stringify(normalized)
    )
  } catch (err) {
    console.warn("[GA4] Failed to save default template map", err)
  }
}

const emitChange = (contextKey, map) => {
  if (!hasWindow()) return
  const detail = {
    contextKey: normalizeContextKey(contextKey),
    map: normalizeMap(map),
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT_NAME, { detail }))
}

const isValidPropertyType = (propertyType) =>
  Object.prototype.hasOwnProperty.call(BASE_MAP, propertyType)

const persistDefaultTemplateSelection = async (
  contextKey,
  propertyType,
  templateId,
) => {
  const { normalizedKey, organizationId, userId } = parseContext(contextKey)
  if (!organizationId && !userId) {
    // Global context falls back to local persistence only.
    return null
  }

  if (!templateId) {
    const { error } = await supabase
      .from("ga4_template_defaults")
      .delete()
      .eq("context_key", normalizedKey)
      .eq("property_type", propertyType)
    if (error) throw error
    return null
  }

  const payload = {
    context_key: normalizedKey,
    property_type: propertyType,
    template_id: templateId,
    organization_id: organizationId,
    user_id: userId,
    updated_at: new Date().toISOString(),
  }

  const { error } = await supabase
    .from("ga4_template_defaults")
    .upsert(payload, { onConflict: "context_key,property_type" })
  if (error) throw error
  return null
}

export const refreshDefaultTemplateMap = async (contextKey) => {
  const { normalizedKey, organizationId, userId } = parseContext(contextKey)
  const fallback = loadDefaultTemplateMap(normalizedKey)
  if (!organizationId && !userId) {
    return fallback
  }

  try {
    const { data, error } = await supabase
      .from("ga4_template_defaults")
      .select("property_type, template_id")
      .eq("context_key", normalizedKey)
    if (error) throw error
    const next = { ...BASE_MAP }
    for (const row of data || []) {
      const propertyType = row?.property_type
      if (isValidPropertyType(propertyType)) {
        next[propertyType] = row?.template_id || null
      }
    }
    saveDefaultTemplateMap(normalizedKey, next)
    emitChange(normalizedKey, next)
    return next
  } catch (err) {
    console.warn(
      `[GA4] Failed to refresh default template map for ${normalizedKey}`,
      err,
    )
    return fallback
  }
}

export const setDefaultTemplateForType = async (
  contextKey,
  propertyType,
  templateId,
) => {
  if (!isValidPropertyType(propertyType)) {
    throw new Error(`Unsupported GA4 property type: ${propertyType}`)
  }
  const { normalizedKey } = parseContext(contextKey)
  try {
    await persistDefaultTemplateSelection(normalizedKey, propertyType, templateId)
  } catch (err) {
    console.warn("[GA4] Failed to persist default template selection", err)
    throw err
  }

  const current = loadDefaultTemplateMap(normalizedKey)
  const next = { ...current, [propertyType]: templateId || null }
  saveDefaultTemplateMap(normalizedKey, next)
  emitChange(normalizedKey, next)
  return next
}

export const clearDefaultTemplateForType = (contextKey, propertyType) => {
  return setDefaultTemplateForType(contextKey, propertyType, null)
}

export const subscribeToDefaultTemplateChanges = (callback) => {
  if (!hasWindow()) return () => {}
  const handler = (event) => {
    if (typeof callback === "function") {
      callback(event.detail || null)
    }
  }
  window.addEventListener(CHANGE_EVENT_NAME, handler)
  return () => {
    window.removeEventListener(CHANGE_EVENT_NAME, handler)
  }
}
